/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  castTabDirection,
  ModalRef,
  Queue,
  QueueStrategy,
  useMicroservices,
  useRuleEngineCache,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  DataRuleDefinition,
  disableTree,
  Entity,
  EntityAcl,
  EntityContext,
  EntityModel,
  FieldType,
  FormCustomTabDefinition,
  FormFieldDefinition,
  FormMode,
  FormModel,
  FormNode,
  FormTabDefinition,
  formTraverse,
  FormViewDefinition,
  GenericFieldValue,
  getControl,
  getEntityModelForFormField,
  ListValue,
  ReloadViewsOptions,
  traverseSiblings,
} from '@msdk/definitions'
import { initializeMetricsAPI } from '@msdk/telemetry'
import {
  Box,
  createComponentWrapperWithRef,
  styled,
  TabEntity,
  Tabs,
  TabsContainer,
  THEME_CONST,
  uiTokens,
} from '@msdk/ui'
import React, { useCallback, useEffect, useState } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { useRecoilState_TRANSITION_SUPPORT_UNSTABLE } from 'recoil'

import { runDataRules, runListDataRules } from '../../hooks/run-data-rules'
import { runOnChange } from '../../hooks/run-onchange'
import { runListValidations, runValidations } from '../../hooks/run-validations'
import { ChangeArgs } from '../../models/form'
import { $entityCollection } from '../../state'
import { arrayToRecord, invisibleFieldsToDataRules, nonWritableFieldsToDataRules } from '../../utils'
import { EmbeddedView } from '../EmbeddedView'
import { EmbeddedViewFederationWrapper } from '../EmbeddedView/EmbeddedViewFederationWrapper'
import { ConditionalFieldRenderer, ConditionalFieldRendererBehaviourProps } from '../Fields'
import { I18nWrapper } from '../I18nWrapper'
import { CustomLogicInvoker } from './custom-logic-invoker'
import { CustomTab } from './CustomTab'
import { EntityView } from './EntityView'
import { Group } from './Group'
import { TabLabel } from './TabLabel'

const MuiFormContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered',
})<{
  $mode: string
  $isTabs?: boolean | undefined
  $centered: boolean | undefined
}>`
  width: 100%;
  height: 100%;
  &:not(:has(.MuiTabs-root):not(:has(& .MuiTabs-root))) {
    padding: ${THEME_CONST.defaultPadding}px;
    padding-right: 0;
  }
`

const NovaFormContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$mode' && prop !== '$centered',
})<{
  $mode: string
  $isTabs?: boolean | undefined
  $centered: boolean | undefined
}>`
  width: 100%;
  height: 100%;
  &:not(:has(.MuiTabs-root):not(:has(& .MuiTabs-root))) {
    padding: ${uiTokens.spacing.padding.large}px;
    padding-right: 0;
  }
`

const FormContainer = createComponentWrapperWithRef(
  {
    mui: MuiFormContainer,
    nova: NovaFormContainer,
  },
  'FormContainer',
)

const trackMetric = initializeMetricsAPI().pushMetric

interface Props {
  root: FormNode
  entity: Entity | null
  additionalEntities: Entity[]
  entityModel: EntityModel
  formModel: FormModel
  entityLoaded: boolean
  acl: EntityAcl
  invoker: CustomLogicInvoker
  mode: FormMode
  modalRef?: ModalRef
  isTabs?: boolean
  onRenderCompleted?: () => void
  onDataChanged?: () => void
}

export function DynamicForm({
  root,
  entity,
  additionalEntities,
  entityModel,
  formModel,
  entityLoaded,
  acl,
  invoker,
  mode,
  modalRef,
  onRenderCompleted,
  onDataChanged,
}: Props) {
  const t = useTranslation()
  const microservices = useMicroservices()

  const [currentEntity, setCurrentEntity] = useState(entity as Entity)
  const [currentAdditionalEntities, setCurrentAdditionalEntities] = useState(additionalEntities)
  const [entityCollection, setEntityCollection] = useRecoilState_TRANSITION_SUPPORT_UNSTABLE($entityCollection)
  const [rootNode, setRootNode] = useState(root)

  const { cachedValidation, cachedDataRules, cachedOnChange } = useRuleEngineCache(entityModel.name, formModel.name)

  const [queue] = useState<Queue>(new Queue(QueueStrategy.Latest))
  const [aclRules, setAclRules] = useState<DataRuleDefinition[]>([])

  const context = useUrlContext()

  const [embeddedViewsRefreshVersion, setEmbeddedViewsRefreshVersion] = useState(0)
  const handleEmbeddedViewDataChanged = () => {
    setEmbeddedViewsRefreshVersion((version) => version + 1)
    onDataChanged?.()
  }

  const [targetedViewVersions, setTargetedViewVersions] = useState<Record<string, number>>({})
  const refreshView = useCallback((viewName: string) => {
    setTargetedViewVersions((prev) => ({ ...prev, [viewName]: (prev[viewName] ?? 0) + 1 }))
  }, [])

  useEffect(() => {
    if (entity?.fields) {
      const currentEntry = entityCollection[entity.internalId as string]
      setCurrentEntity({ ...entity })
      setCurrentAdditionalEntities([...additionalEntities])
      setEntityCollection((currentEntityCollection) => {
        const current = currentEntityCollection[entity.internalId as string]
        return {
          ...currentEntityCollection,
          [entity.internalId as string]: {
            entity,
            additionalEntities: currentAdditionalEntities,
            rootNode,
            fieldValidations: [],
            pendingChanges: !!currentEntry?.pendingChanges,
            acl: current?.acl as EntityAcl,
          },
        }
      })
    }
    return () => {
      setEntityCollection((currentCollection) => {
        const newCollection = { ...currentCollection }
        if (entity?.fields) {
          delete newCollection[entity.internalId as string]
        }
        return newCollection
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity])

  useEffect(() => {
    if (entityLoaded && entity) {
      const fetch = async () => {
        const newEntity = { ...entity, fields: { ...entity.fields } }
        const additionals = currentAdditionalEntities.map((e) => {
          return { ...e, fields: { ...e.fields } }
        })
        const clonedAdditionals = currentAdditionalEntities.map((e) => {
          return { ...e, fields: { ...e.fields } }
        })
        const clonedEntity = { ...entity, fields: { ...entity.fields } }

        const invisibleRules = invisibleFieldsToDataRules(acl.invisibleFields)
        const readonlyRules = nonWritableFieldsToDataRules(acl.nonWritableFields)
        const aclRules = [...invisibleRules, ...readonlyRules]
        setAclRules(aclRules)

        const entityContext = new EntityContext(newEntity, root, additionals, formModel)
        const clonedEntityContext = new EntityContext(clonedEntity, root, clonedAdditionals, formModel)

        const startTime = new Date().getUTCMilliseconds()
        console.groupCollapsed('After Entity Load')
        console.time('Running Rule engine')
        console.log('Context information: ')
        console.log('%o the current form object', root)
        console.log('%o the updated entity', entityContext)
        await invoker.postLoad(entityContext)

        // here keep validation is true to allow validations
        // coming from server to be shown, whe still have to
        // reset the tree state to allow proper execution of
        // data rules that alter visibility or readonly state
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        resetFormTree(entityContext, true)

        // pass undefined so runDataRulesAndValidations reads pendingChanges from current state
        // via functional setter, avoiding a stale closure after postLoad may call rebindEntity
        await runDataRulesAndValidations(entityContext, clonedEntityContext, acl, undefined, aclRules)

        const endTime = new Date().getUTCMilliseconds()
        console.timeEnd('Running Rule engine')
        console.groupEnd()

        trackMetric({
          value: endTime - startTime,
          attributes: { formName: formModel.name, entityName: entityModel.name },
        })

        if (!acl.canUpdate || mode === 'Readonly') {
          disableTree(root)
        }
      }
      fetch()
        .then(() => onRenderCompleted?.())
        .catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityLoaded, root, mode])

  const tabsDirection = castTabDirection(formModel.tabDirection)

  const onFieldChange = async (args: ChangeArgs) => {
    const newEntity = { ...currentEntity, fields: { ...currentEntity.fields } }
    const additionals = currentAdditionalEntities.map((e) => {
      return { ...e, fields: { ...e.fields } }
    })

    const entityContext = new EntityContext(newEntity, root, additionals, formModel)
    entityContext.fields[args.fieldName] = args.value
    const unboxed = entityContext.unbox()

    setCurrentEntity(unboxed.entity)
    setCurrentAdditionalEntities(unboxed.additionalEntities)

    /*
     * using a queue for field change events prevents multiple events to execute concurrently
     * in case of a form with heavy async custom logic this ensures that multiple events don't conflict
     * with each other and that only the last triggered event is applying the correct effects form custom logic
     */
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    queue.enqueue(() => onChangeTask(args, unboxed.entity, unboxed.additionalEntities))
  }

  const onChangeTask = async (args: ChangeArgs, newEntity: Entity, additionalEntities: Entity[]) => {
    const startTime = new Date().getMilliseconds()
    console.groupCollapsed('OnChange')
    console.time('Running Rule engine')
    console.log('Context information: ')
    console.log('%o the current form object', root)
    console.log('%o the updated entity', newEntity)

    //onChange
    const entityContext = new EntityContext(newEntity, root, additionalEntities, formModel)

    runOnChange(entityContext, cachedOnChange, args.fieldName, entityModel, formModel)
    await invoker.onFieldChanged(entityContext, args)
    const clonedEntity = { ...newEntity, fields: { ...newEntity.fields } }
    const clonedAdditionals = additionalEntities.map((e) => {
      return { ...e, fields: { ...e.fields } }
    })
    const clonedEntityContext = new EntityContext(clonedEntity, root, clonedAdditionals, formModel)

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    resetFormTree(entityContext)

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    await runDataRulesAndValidations(entityContext, clonedEntityContext, undefined, true)

    console.timeEnd('Running Rule engine')
    console.groupEnd()
    const endTime = new Date().getMilliseconds()
    trackMetric({
      value: endTime - startTime,
      attributes: { formName: formModel.name, entityName: entityModel.name },
    })
  }

  /**
   * retrieves the node value considering that a cell of a row inside a listfield
   * cannot access the entity directly but has to first retrieve the list value
   */
  const getNodeValue = (entity: EntityContext, node: FormNode): GenericFieldValue => {
    if (node.value.listName) {
      const listValue = entity.fields[node.value.listName] as ListValue
      const rowId = node.value.rowId as string
      const row = listValue.value?.find((r) => r.id === rowId) as Entity
      return row.fields[node.id]
    }
    return entity.fields[node.id] !== undefined ? entity.fields[node.id] : node.value.data
  }

  const resetFormTree = (entityContext: EntityContext, keepValidations?: boolean) => {
    formTraverse(root, (node) => {
      if (node.value.fieldDefinition?.fieldDefinitionType === 'List') {
        const newControl = getControl(
          {
            entity: entityContext,
            fieldDef: node.value.fieldDefinition,
            formDef: node.value.formDefinition as FormFieldDefinition,
            prevChildren: undefined,
          },
          entityModel,
          formModel,
          formModel.name,
        )
        node.value = newControl.value
        node.firstChild = newControl.firstChild
      }

      node.value.validationMessages = keepValidations ? node.value.validationMessages : []
      node.value.validationParams = keepValidations ? node.value.validationParams : []
      node.value.state = 'Enabled'
      node.value.visibility = 'Visible'
      node.value.validationState = 'Pending'
      node.value.isDirty = false
      node.value.selectOptions = { keep: [], remove: [] }
      node.value.validationFlags = 0
      node.value.data = getNodeValue(entityContext, node)
      if (node.value.fieldDefinition) {
        const fieldName = node.value.fieldDefinition.name
        const formFieldName = (node.value.formDefinition as FormFieldDefinition)?.name
        const model = getEntityModelForFormField(formFieldName, entityModel, formModel)
        const rval = model.getNumericValidation(fieldName) || formModel.getNumericValidation(formFieldName)
        node.value.range = undefined
        if (rval) {
          node.value.range = {
            min: rval.min,
            max: rval.max,
          }
        }
      }
    })
  }

  const runDataRulesAndValidations = async (
    entityContext: EntityContext,
    clonedEntityContext: EntityContext,
    acl?: EntityAcl,
    pendingChanges?: boolean,
    aclRulesSet?: DataRuleDefinition[],
  ) => {
    //dataRules
    const rCache = { ...cachedDataRules }
    rCache.rules = [...rCache.rules, ...(aclRulesSet ?? aclRules)]
    runDataRules(root, entityContext, rCache)
    runListDataRules(root, clonedEntityContext, cachedDataRules)

    //validation
    runValidations(root, entityContext, cachedValidation)
    runListValidations(root, entityContext, cachedValidation)
    await invoker.validate(entityContext)
    const customVal = entityContext.customValidaitons.filter((v) => !v.onList)
    const onListCustomVal = entityContext.customValidaitons.filter((v) => v.onList)
    const clValidations = {
      ...cachedValidation,
      cachedRules: arrayToRecord(customVal, 'field'),
      cachedListRules: arrayToRecord(onListCustomVal, 'onList'),
    }
    runValidations(root, entityContext, clValidations)
    runListValidations(root, entityContext, clValidations)

    //dataRules
    runDataRules(root, clonedEntityContext, rCache, true)
    runListDataRules(root, clonedEntityContext, cachedDataRules, true)

    //update state
    const unboxed = entityContext.unbox()
    unstable_batchedUpdates(() => {
      setCurrentEntity(unboxed.entity)
      setCurrentAdditionalEntities(unboxed.additionalEntities)
      setRootNode({ ...root })
      setEntityCollection((current) => ({
        ...current,
        [unboxed.entity.internalId as string]: {
          entity: unboxed.entity,
          additionalEntities: unboxed.additionalEntities,
          rootNode: root,
          pendingChanges:
            pendingChanges !== undefined
              ? pendingChanges
              : !!current[unboxed.entity.internalId as string]?.pendingChanges,
          acl: acl ?? current[unboxed.entity.internalId as string]?.acl,
        },
      }))
    })
  }

  /**
   * The function `isMsOnline` checks if a microservice with a specific name is online.
   * @param {string} msName - The `msName` parameter is a string that represents the name of a
   * microservice.
   * @returns The function `isMsOnline` returns a boolean value indicating whether the microservice
   * with the specified name is online and is in a healthy state. If the microservice with the given
   * name is found in the `microservices` array and its `isOk` property is true, the function returns
   * `true`. Otherwise, it returns `false`.
   */
  const isMsOnline = (msName: string) => {
    const msEntry = microservices.find((ms) => ms.name === msName)
    return msEntry && msEntry.isOk
  }

  const buildLayout = (node: FormNode | undefined): JSX.Element[] => {
    if (!node) {
      return []
    }

    let result: JSX.Element[] = []
    let sibling: JSX.Element[] = []

    switch (node.value.type) {
      case 'Group': {
        const childs = buildLayout(node.firstChild)
        sibling = buildLayout(node.nextSibling)
        result.push(
          <Group key={node.id} node={node}>
            {childs}
          </Group>,
        )
        break
      }
      case 'Tab':
      case 'CustomTab': {
        const tabs: FormNode[] = []
        traverseSiblings(node, (e) => {
          if (e.value.visibility === 'Visible') tabs.push(e)
        })
        result.push(
          <TabsContainer key={node.id} $withPadding orientation={tabsDirection}>
            <Tabs
              orientation={tabsDirection}
              startingTab={context.context.ft ? parseInt(context.context.ft) : 0}
              childs={tabs
                .map((e) => {
                  const def = e.value.formDefinition as FormTabDefinition
                  if (def.type === 'Tab') {
                    const childs = buildLayout(e.firstChild)
                    return {
                      label: <TabLabel def={def} formName={formModel.name} entityName={entityModel.name} />,
                      content: childs,
                    } as TabEntity
                  } else if (def.type === 'CustomTab') {
                    const customDef = e.value.formDefinition as FormCustomTabDefinition
                    //we want to show the custom tab only if the ms is online
                    if (isMsOnline(customDef.msProvider)) {
                      return {
                        label: t(def.idml, def.description),
                        content: <CustomTab tabDef={customDef} />,
                        isCustom: true,
                      } as TabEntity
                    }
                  }
                })
                .filter((e) => !!e)}
            />
          </TabsContainer>,
        )
        break
      }
      case 'View': {
        const def = node.value.formDefinition as FormViewDefinition
        sibling = buildLayout(node.nextSibling)
        if (entity && node.value.visibility === 'Visible') {
          result.push(
            <EmbeddedViewFederationWrapper
              entityName={def.entityName}
              moduleName={def.name}
              key={`${def.entityName}-${def.name}`}
            >
              <I18nWrapper entityName={def.entityName} moduleName={def.name}>
                <EmbeddedView
                  key={`${def.entityName}-${def.name}`}
                  entityName={def.entityName}
                  viewName={def.name}
                  containerEntity={entity}
                  viewDef={def}
                  modalRef={modalRef}
                  refreshVersion={embeddedViewsRefreshVersion + (targetedViewVersions[def.name] ?? 0)}
                  refreshView={refreshView}
                  onDataChanged={handleEmbeddedViewDataChanged}
                />
              </I18nWrapper>
            </EmbeddedViewFederationWrapper>,
          )
        }

        break
      }
      case 'EntityView': {
        sibling = buildLayout(node.nextSibling)
        result.push(<EntityView key={node.id} node={node} entity={currentEntity} />)
        break
      }
      default: {
        sibling = buildLayout(node.nextSibling)

        const behaviour: ConditionalFieldRendererBehaviourProps =
          mode === 'Readonly' ? { readonly: true } : { onChange: onFieldChange }

        const reloadViews = (options?: ReloadViewsOptions) => {
          if (options) {
            options.views.forEach((viewName) => refreshView?.(viewName))
          }
        }
        result.push(
          <ConditionalFieldRenderer
            {...behaviour}
            key={node.id}
            node={node}
            type={node.value.type as FieldType}
            onReload={reloadViews}
          />,
        )

        break
      }
    }

    result = result.concat(...sibling)

    return result
  }

  return (
    <FormContainer $mode={mode} $centered={formModel?.isCentered()}>
      {buildLayout(rootNode).map((e) => e)}
    </FormContainer>
  )
}
